import BlockContent from '@sanity/block-content-to-react';
import { Layout } from 'components/layout';
import Seo from 'components/seo';
import { Button } from 'components/tsxCommon/Button';
import { YoutubeEmbed } from 'components/tsxCommon/YoutubeEmbed';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';

import { RelatedResources } from '../components/tsxCommon/RelatedResources';

const getParticipants = (participants) => {
    return (
        <div className="tw-flex tw-gap-x-48 tw-gap-y-12 tw-flex-col tw-mt-12">
            {participants.map((participant, index) => {
                const { name, image, position, company } = participant;
                return (
                    <div
                        key={index}
                        className="tw-pl-[110px] tw-relative tw-flex tw-items-center tw-h-[100px]"
                    >
                        <GatsbyImage
                            image={image.asset.gatsbyImageData}
                            alt={name}
                            className="tw-w-[100px] tw-h-[100px] tw-rounded tw-absolute tw-left-0 tw-border"
                        />
                        <div>
                            <p className="tw-m-0 tw-text-lg">{name}</p>
                            <p className="tw-m-0 tw-text-sm tw-text-neutral-subdued">{position}</p>
                            <p className="tw-m-0 tw-text-sm tw-text-neutral-subdueds">{company}</p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const Webinar = (props) => {
    const {
        title,
        subtitle,
        metaData,
        youtubeEmbedId,
        _rawParagraph,
        speakers,
        moderators,
        resourcePicker,
        signupLink,
        image
    } = props.data.sanityWebinar;

    return (
        <div className="pageWrapper">
            <Seo
                title={`${metaData.title} | Webinars | SalesScreen`}
                description={metaData.description}
                img={metaData.pageImage.asset.gatsbyImageData.images.fallback.src}
            />
            <Layout darkNav={true}>
                <div className="container">
                    <div className="tw-mx-auto tw-prose tw-pt-[100px]">
                        <h1>{title}</h1>
                        <p>{subtitle}</p>
                        {signupLink ? (
                            <div className="tw-relative">
                                <GatsbyImage
                                    image={image.asset.gatsbyImageData}
                                    alt={title}
                                    className="tw-w-full tw-inline-block"
                                />
                                <div className="tw-flex tw-justify-center tw-py-24">
                                    <Button
                                        text="Register Now"
                                        variant="primary"
                                        onDarkBackground={true}
                                        url={signupLink}
                                        buttonVariant="externalLink"
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>

                {!signupLink ? (
                    <div className="tw-mx-auto tw-relative tw-my-64 lg:tw-w-[1000px] md:tw-w-[700px] tw-w-full">
                        <div className="tw-my-64 tw-w-full">
                            <YoutubeEmbed
                                videoId={youtubeEmbedId}
                                classNames="tw-w-full tw-aspect-video"
                            />
                        </div>
                    </div>
                ) : null}
                <div className="container">
                    <div className="tw-mx-auto tw-prose">
                        <div className="tw-text-left tw-inline-block">
                            <div className="tw-flex tw-flex-wrap tw-gap-48">
                                <div>
                                    <p className="tw-text-xl">Speakers</p>
                                    {getParticipants(speakers)}
                                </div>
                                <div>
                                    {moderators.length ? (
                                        <>
                                            <p className="tw-text-xl">Moderators</p>
                                            {getParticipants(moderators)}
                                        </>
                                    ) : null}
                                </div>
                            </div>
                            <div className="tw-my-48 tw-prose">
                                <BlockContent blocks={_rawParagraph} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="tw-container tw-mx-auto tw-px-24">
                    {resourcePicker && resourcePicker.itemList.length ? (
                        <RelatedResources relatedResources={resourcePicker.itemList} />
                    ) : null}
                </div>
            </Layout>
        </div>
    );
};

export default Webinar;

export const query = graphql`
    query Webinar($title: String!) {
        sanityWebinar(title: { eq: $title }) {
            title
            subtitle
            youtubeEmbedId
            image {
                asset {
                    gatsbyImageData(width: 1920, placeholder: NONE)
                }
            }
            metaData {
                description
                title
                pageImage {
                    asset {
                        gatsbyImageData
                    }
                }
            }
            moderators {
                image {
                    asset {
                        gatsbyImageData(width: 200, placeholder: NONE)
                    }
                }
                company
                name
                position
            }
            speakers {
                image {
                    asset {
                        gatsbyImageData(width: 200, placeholder: NONE)
                    }
                }
                company
                position
                name
            }
            _rawParagraph
            _id
            signupLink
            resourcePicker {
                itemList {
                    ... on SanityBlogPost {
                        id
                        _type
                        identifier
                        localizedTitle: title {
                            en
                        }
                        description {
                            en
                        }
                        image {
                            asset {
                                gatsbyImageData(width: 300, placeholder: BLURRED)
                            }
                        }
                        _rawSlug
                    }
                    ... on SanityIntegration {
                        id
                        _type
                        title
                        logo {
                            asset {
                                gatsbyImageData(width: 300, placeholder: BLURRED)
                            }
                        }
                        description {
                            en
                        }
                    }
                    ... on SanityResource {
                        id
                        _type
                        identifier
                        localizedTitle: title {
                            en
                        }
                        contentCoverImage {
                            asset {
                                gatsbyImageData(width: 300, placeholder: BLURRED)
                            }
                        }
                        description {
                            en
                        }
                    }
                    ... on SanityWebinar {
                        id
                        _type
                        title
                        image {
                            asset {
                                gatsbyImageData(width: 300, placeholder: BLURRED)
                            }
                        }
                        subtitle
                    }
                    ... on SanityCustomerStory {
                        id
                        _type
                        image {
                            asset {
                                gatsbyImageData(width: 300, placeholder: BLURRED)
                            }
                        }
                        customerAndUs {
                            en
                        }
                        companyName
                    }
                }
            }
        }
    }
`;
